import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch, detailMessage } from '../api';
import InfoTip from '../components/InfoTip';
import { useCrumbLabel } from '../components/shell/crumbs';
import { Icon, PageHeader, SkeletonText, Tabs } from '../components/ui';

/* A user's complete system prompt, coloured by the file each part of it came from.

   The console's copy of the website's Instructions view (openpaw.website,
   screens/Instructions.jsx): the same three runs as tabs, the same colour per file, and
   a key that jumps to where each file starts. Two differences, both deliberate.

   The regions come from the platform. The website splits one string by looking for each
   file's heading, and has to read the last file to find where it ends. `segments` says
   where every region starts and ends, so nothing here is inferred and no file is read
   for it. MEMORY.md in particular is never read: its section is a placeholder.

   The text is shown as it is sent, in a `pre`, not rendered as markdown. This app has no
   sanitiser, and most of the document is the user's and the agent's own writing. */

// The runs the platform assembles a prompt for. The ids go on the wire as they are: an
// unknown one is a 400, not a default.
const AUDIENCES = [
  {
    value: 'main',
    label: 'Main session',
    intro: "Everything the agent is told before the user's message, in the order it reads it.",
  },
  {
    value: 'spawn',
    label: 'Sub-agent session',
    intro: 'What a background helper or a scheduled task is told.',
  },
  {
    value: 'subtask',
    label: 'Workflow task',
    intro: 'What one step of a multi-step job is told.',
  },
];

const PLATFORM_LABEL = 'Added by the platform';

// The website's hue per file (lib/promptFiles.js), so a file is one colour in both places.
// Any other name hashes to a hue that stays the same from one render to the next.
const HUES = {
  'SOUL.md': 266,
  'AGENTS.md': 212,
  'IDENTITY.md': 322,
  'USER.md': 24,
  'MEMORY.md': 162,
  'PREFERENCES.md': 130,
  'TOOLS.md': 44,
  'SKILLS.md': 72,
  'MAIN.md': 100,
  'HEARTBEAT.md': 352,
  'BOOTSTRAP.md': 188,
};

const hueFor = (name) => {
  if (HUES[name] !== undefined) return HUES[name];
  const text = String(name || '?');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 360;
  return hash;
};

// UTF-8 bytes, which is what the platform's total counts: an accented letter is two.
const byteLength = (text) => new TextEncoder().encode(String(text ?? '')).length;

const formatBytes = (bytes) => {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} bytes`;
  const kb = bytes / 1024;
  return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
};

function UserInstructions() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [audience, setAudience] = useState('main');
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const docRef = useRef(null);

  useCrumbLabel(user?.username);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/api/users/${userId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled && data) setUser(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  // Switching tabs clears the document and asks again. `cancelled` stops a slow answer
  // for the tab just left from landing under the one just picked.
  useEffect(() => {
    let cancelled = false;
    setDoc(null);
    setError(null);
    (async () => {
      try {
        const resp = await apiFetch(
          `/api/users/${userId}/prompt-files/assembled?audience=${encodeURIComponent(audience)}`);
        const body = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (resp.ok) setDoc(body);
        else setError(detailMessage(body.detail, 'Could not assemble the system prompt'));
      } catch {
        if (!cancelled) setError('Could not assemble the system prompt');
      }
    })();
    return () => { cancelled = true; };
  }, [userId, audience, attempt]);

  const segments = useMemo(() => doc?.segments || [], [doc]);

  // One row per source, in the order the document first reaches it, with everything it
  // contributes. `index` is that first region, which is where the row jumps to.
  const legend = useMemo(() => {
    const rows = new Map();
    segments.forEach((segment, index) => {
      const key = segment.source || PLATFORM_LABEL;
      const row = rows.get(key);
      if (row) row.bytes += byteLength(segment.text);
      else rows.set(key, { key, index, platform: !segment.source, bytes: byteLength(segment.text) });
    });
    return [...rows.values()];
  }, [segments]);

  // The document's own scrollTop, not scrollIntoView, which scrolls the page as well and
  // takes the key just clicked off screen. Focus follows, so the arrow keys then move
  // through the document rather than the page behind it.
  const jumpTo = useCallback((index) => {
    const box = docRef.current;
    const target = box?.children?.[index];
    if (!target) return;
    box.scrollTop += target.getBoundingClientRect().top - box.getBoundingClientRect().top;
    box.focus({ preventScroll: true });
  }, []);

  const loading = !doc && !error;
  const shown = AUDIENCES.find((a) => a.value === (doc?.audience || audience)) || AUDIENCES[0];
  const name = user?.username || `User ${userId}`;

  return (
    <div>
      <PageHeader
        // The trail is the way back on phones, where the top bar shows only the last crumb.
        eyebrow={(
          <>
            <Link to="/users">Users</Link>
            <Icon name="chevronRight" size={12} />
            <Link to={`/users/${userId}`}>{name}</Link>
          </>
        )}
        description={`${shown.intro} Assembled just now from this user's workspace.`}
        actions={(
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setAttempt((n) => n + 1)}
            disabled={loading}
          >
            {loading ? <><span className="spinner-sm" /> Loading…</> : <><Icon name="refresh" /> Refresh</>}
          </button>
        )}
      >
        <h1 className="page-title">
          Instructions
          <InfoTip label="What this shows" width={340}>
            The agent's whole system prompt: the platform's own sections and this user's
            workspace files, in the order the agent reads them. Each colour is one file and
            grey is the platform's. MEMORY.md is never read here, so its section holds a
            placeholder.
          </InfoTip>
        </h1>
      </PageHeader>

      {/* Outside the loading gate, so the control just used stays put while it loads. */}
      <Tabs
        tabs={AUDIENCES}
        value={audience}
        onChange={setAudience}
        variant="line"
        label="Which run to show the prompt for"
        idPrefix="ip"
      />

      <div
        className="ip-panel"
        role="tabpanel"
        id={`ip-panel-${audience}`}
        aria-labelledby={`ip-tab-${audience}`}
      >
        {error ? (
          <div className="notice notice-danger" role="alert">
            <Icon name="alertCircle" />
            <div className="notice-body">{error}</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
          </div>
        ) : null}

        {loading ? <SkeletonText lines={12} /> : null}

        {doc ? (
          <>
            {/* Written for this console: a build the copy was not checked against, a user
                with no workspace yet, and a child run's per-run additions. */}
            {doc.warnings?.length ? (
              <div className="notice" role="note">
                <Icon name="info" />
                <div className="notice-body">
                  {doc.warnings.map((w) => <p key={w} className="ip-warning">{w}</p>)}
                </div>
              </div>
            ) : null}

            {/* The list carries the verb, so each button's name is the file and its size. */}
            <ul className="ip-legend" aria-label="Jump to a part of the prompt">
              {legend.map((row) => (
                <li key={row.key}>
                  <button
                    type="button"
                    className={`ip-legend-row${row.platform ? ' is-platform' : ''}`}
                    style={row.platform ? undefined : { '--ip-hue': hueFor(row.key) }}
                    onClick={() => jumpTo(row.index)}
                  >
                    <span className="ip-swatch" aria-hidden="true" />
                    <span className="ip-legend-name">{row.key}</span>
                    <span className="ip-legend-size">{formatBytes(row.bytes)}</span>
                  </button>
                </li>
              ))}
            </ul>

            {/* One scroller for the whole document, so the strips form one column down the
                side. Its children are the regions, in order: `jumpTo` indexes them. */}
            <div
              className="ip-doc"
              ref={docRef}
              tabIndex={0}
              role="group"
              aria-label="The assembled system prompt"
            >
              {segments.map((segment, index) => {
                const label = segment.source || PLATFORM_LABEL;
                return (
                  <div
                    // The index too: the platform's text appears in several regions.
                    key={`${segment.source || 'platform'}-${index}`}
                    className={`ip-seg${segment.source ? '' : ' is-platform'}`}
                    style={segment.source ? { '--ip-hue': hueFor(segment.source) } : undefined}
                    role="group"
                    aria-label={label}
                  >
                    <span className="ip-strip" title={label} aria-hidden="true" />
                    <pre className="ip-text">{segment.text}</pre>
                  </div>
                );
              })}
            </div>

            <p className="ip-total">
              {formatBytes(doc.bytes)} in total
              {doc.fidelity === 'exact' ? ' · checked against the agent version this user runs' : ''}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default UserInstructions;
